import { ILoan, ILoanDetail, IMultipleProperty } from 'pages/LoanStructure/interfaces'
import { analyzeLoanStructureValue, isNonDSCRProduct } from 'pages/LoanStructure/Logic'
import { store } from 'reducers'
import { getPrice2decimal, removeComma } from 'utils'
import { getInterestOrNot } from 'utils/getInterestOrNot'

export function combinedMonthlyExpense(loan: ILoan, rateData: any) {
  const interest_rate = removeComma(rateData.rate)
  const pricingType = rateData.type

  const {
    proposedLoanAmount,
    proposedMonthlyInsurance,
    proposedMonthlyTaxes,
    proposedMonthlyHoaDues,
    proposedMonthlyRent,
    floodInsurance,
    schoolTax,
    otherTax,
    secondLienPayment,
    rehabBudget,
  } = analyzeLoanStructureValue(loan)

  const monthsReserve = removeComma(loan.monthsReserve)

  let mortgage_payment = (proposedLoanAmount * interest_rate) / 100
  mortgage_payment = mortgage_payment / 12
  let c_text1 = 'Monthly Interest Payment'

  // Calculate Principal and Interest payment for DSCR products
  if (!isNonDSCRProduct(loan.productType)) {
    const state = store.getState()
    const loanDetail = state.loanDetail as ILoanDetail
    const pricingEngineIsOff = !loanDetail?.pricingEngine
    const IsInterestOnly = loan?.amortizationType?.includes('IO')
    const shouldCalculatePrincipalAndInterest = pricingEngineIsOff
      ? !IsInterestOnly
      : !!pricingType && !getInterestOrNot(pricingType)

    if (shouldCalculatePrincipalAndInterest) {
      c_text1 = 'Monthly Principal and Interest'
      const loanTerm = removeComma(loan.loanTerm)
      const interest = interest_rate / 100
      const value = Math.pow(1 + interest / 12, loanTerm)
      mortgage_payment = Number((((proposedLoanAmount * interest) / 12) * value) / (value - 1))
    }
  }

  let Rlt: any = {
    title: c_text1,
    mortgagePayment: getPrice2decimal(Number(mortgage_payment.toFixed(2))),
    proposedMonthlyInsurance: getPrice2decimal(proposedMonthlyInsurance),
    proposedMonthlyTaxes: getPrice2decimal(proposedMonthlyTaxes),
    proposedMonthlyHoaDues: getPrice2decimal(proposedMonthlyHoaDues),
    proposedMonthlyRent: getPrice2decimal(proposedMonthlyRent),
    secondLienPayment: getPrice2decimal(secondLienPayment),
    floodInsurance: getPrice2decimal(floodInsurance),
    schoolTax: getPrice2decimal(schoolTax),
    otherTax: getPrice2decimal(otherTax),
    rehabBudget: getPrice2decimal(rehabBudget),
  }

  const sum =
    removeComma(Rlt.mortgagePayment) +
    removeComma(Rlt.proposedMonthlyInsurance) +
    removeComma(Rlt.proposedMonthlyTaxes) +
    removeComma(Rlt.proposedMonthlyHoaDues) +
    removeComma(Rlt.secondLienPayment) +
    removeComma(Rlt.floodInsurance) +
    removeComma(Rlt.schoolTax) +
    removeComma(Rlt.otherTax)

  Rlt.sum = getPrice2decimal(sum)

  const rtlTraderPercent = 0 / 100
  Rlt.requiredReserves = getPrice2decimal((Number(sum) * monthsReserve + rehabBudget * rtlTraderPercent).toFixed(2))

  let dscr: any = null
  if (interest_rate > 0) {
    dscr = removeComma(proposedMonthlyRent) / removeComma(sum)
    dscr = getPrice2decimal(dscr)
  }
  Rlt.dscr = dscr
  return Rlt
}

export function combinedPropertyMonthlyExpense(loan: ILoan, propertyData: IMultipleProperty, rateData: any) {
  const interest_rate = removeComma(rateData.rate)
  const pricingType = rateData.type

  const proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  const rehabBudget = removeComma(loan.rehabBudget)
  const monthsReserve = removeComma(loan.monthsReserve)
  const proposedMonthlyInsurance = removeComma(propertyData.proposedMonthlyInsurance)
  const proposedMonthlyTaxes = removeComma(propertyData.proposedMonthlyTaxes)
  const proposedMonthlyHoaDues = removeComma(propertyData.proposedMonthlyHoaDues)
  const proposedMonthlyRent = removeComma(propertyData.proposedMonthlyRent)
  const floodInsurance = removeComma(propertyData.floodInsurance)
  const schoolTax = removeComma(propertyData.schoolTax)
  const otherTax = removeComma(propertyData.otherTax)
  let secondLienPayment = 0
  if (removeComma(propertyData.secondLien) > 0) secondLienPayment = removeComma(propertyData.secondLienPayment)

  let mortgage_payment = (proposedLoanAmount * interest_rate) / 100
  mortgage_payment = mortgage_payment / 12
  let c_text1 = 'Monthly Interest Payment'

  if (!isNonDSCRProduct(loan.productType)) {
    const state = store.getState()
    const loanDetail = state.loanDetail as ILoanDetail
    const pricingEngineIsOff = !loanDetail?.pricingEngine
    const IsInterestOnly = loan?.amortizationType?.includes('IO')
    const shouldCalculatePrincipalAndInterest = pricingEngineIsOff
      ? !IsInterestOnly
      : !!pricingType && !getInterestOrNot(pricingType)

    if (shouldCalculatePrincipalAndInterest) {
      c_text1 = 'Monthly Principal and Interest'
      const loanTerm = removeComma(loan.loanTerm)
      const interest: number = interest_rate / 100
      const value = Math.pow(1 + interest / 12, loanTerm)
      mortgage_payment = Number((((proposedLoanAmount * interest) / 12) * value) / (value - 1))
    }
  }

  let Rlt: any = {
    title: c_text1,
    mortgagePayment: getPrice2decimal(Number(mortgage_payment.toFixed(2))),
    proposedMonthlyInsurance: getPrice2decimal(proposedMonthlyInsurance),
    proposedMonthlyTaxes: getPrice2decimal(proposedMonthlyTaxes),
    proposedMonthlyHoaDues: getPrice2decimal(proposedMonthlyHoaDues),
    secondLienPayment: getPrice2decimal(secondLienPayment),
    floodInsurance: getPrice2decimal(floodInsurance),
    schoolTax: getPrice2decimal(schoolTax),
    otherTax: getPrice2decimal(otherTax),
    rehabBudget: getPrice2decimal(rehabBudget),
  }

  const sum =
    removeComma(Rlt.mortgagePayment) +
    removeComma(Rlt.proposedMonthlyInsurance) +
    removeComma(Rlt.proposedMonthlyTaxes) +
    removeComma(Rlt.proposedMonthlyHoaDues) +
    removeComma(Rlt.secondLienPayment) +
    removeComma(Rlt.floodInsurance) +
    removeComma(Rlt.schoolTax) +
    removeComma(Rlt.otherTax)

  Rlt.sum = getPrice2decimal(Number(sum.toFixed(2)))

  const rtlTraderPercent = 0 / 100
  Rlt.requiredReserves = getPrice2decimal((Number(sum) * monthsReserve + rehabBudget * rtlTraderPercent).toFixed(2))

  let dscr: any = null
  if (interest_rate > 0) {
    dscr = removeComma(proposedMonthlyRent) / removeComma(sum)
    dscr = getPrice2decimal(dscr)
  }
  Rlt.dscr = dscr
  return Rlt
}
